import { useEffect } from "react";
import { motion } from "framer-motion";
import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Sparkles, Zap, Shield, Clock } from "lucide-react";
import appIcon from "@/assets/app-icon.png";

interface Props {
  onGetStarted: () => void;
}

const benefits = [
  { icon: Zap, title: "One-Click Apply", desc: "Auto-fill applications with your saved profile" },
  { icon: Sparkles, title: "AI Cover Letters", desc: "Generate tailored cover letters in seconds" },
  { icon: Clock, title: "Save Hours", desc: "Stop retyping the same fields on every application" },
  { icon: Shield, title: "Your Data, Secured", desc: "Encrypted storage with full privacy control" },
];

export default function SplashPage({ onGetStarted }: Props) {
  useEffect(() => {
    trackEvent("extension_installed");
  }, []);

  const handleGetStarted = () => {
    trackEvent("quick_setup_started");
    onGetStarted();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Hero */}
      <div className="px-6 pt-8 pb-6 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <img src={appIcon} alt="Jobily" className="w-16 h-16 rounded-2xl mx-auto mb-4" />
          <h2 className="font-display font-bold text-2xl mb-2">Land Your Dream Job</h2>
          <p className="text-sm opacity-90">The smartest way to apply for jobs. Set up once, apply everywhere.</p>
        </motion.div>
      </div>

      {/* Benefits */}
      <div className="flex-1 px-5 py-6 grid grid-cols-2 gap-3">
        {benefits.map((b, i) => (
          <motion.div
            key={b.title}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + i * 0.1 }}
            className="flex flex-col items-center text-center gap-2 p-3 rounded-xl bg-muted/50 border border-border"
          >
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <b.icon className="w-4.5 h-4.5 text-primary" />
            </div>
            <div>
              <h4 className="font-display font-semibold text-sm">{b.title}</h4>
              <p className="text-xs text-muted-foreground">{b.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* CTA */}
      <div className="px-5 py-5 border-t border-border">
        <Button className="w-full gap-2 h-12 text-base font-semibold" onClick={handleGetStarted}>
          Get Started <Sparkles className="w-4 h-4" />
        </Button>
        <p className="text-[11px] text-muted-foreground text-center mt-2">
          30 seconds to set up · No account needed
        </p>
      </div>
    </div>
  );
}
